import { useCallback, useRef, useState } from 'react';
import { useInMemoryFilteredList } from '../../../../shared/hooks/useInMemoryFilteredList';
import { matchPattern } from '../../../../shared/utils/string/pattern';
import { useQuery } from '../../../../request/hooks/useQuery';
import {
  FieldsSelectionUpdatesPayload,
  ObjectField,
  ObjectFieldsRawData,
  ObjectFieldStatus
} from '../../interfaces/object-field';
import { ObjectsConfigAPI } from '../../../api/ObjectsConfigAPI';
import { ObjectFieldsFactory, ObjectFieldsMetaDataFactory } from '../../factories/fields';
import { Integration } from '../../../../shared/interfaces/integration';
import { ObjectConfig } from '../../interfaces/object';
import { OffsetPaginationStrategy } from '../../../../list-conductor/offset-pagination-strategy';
import { PaginationData } from '../../../../pagination-controls/interfaces';
import { useMutation } from '../../../../request/hooks/useMutation';
import { ObjectFactory } from '../../factories/objects';
import { FilterList, ListNextPage, ListPrevPage } from '../../../../list-conductor/list-actions';
import { ObjectFieldsMetaData } from '../../interfaces/object-field-header';
import { DEFAULT_LIMIT } from '../../../../list-conductor/constants';
import { DEFAULT_STRING_OPERATOR } from '../../../../filter/constants';
import { SearchValueType } from '../../../../input/SearchArea/interface';
import { getSearchValue } from '../../../../shared/utils/filters';
import { ListConductorRawFilter } from '../../../../list-conductor/filters';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { PipelineObjectConfigTrackers } from '../../../tracking/pipeline-object-config-trackers';

export const OBJECT_FIELDS_LIST_SEARCH_KEY = 'term';

interface UseFieldsConfigProps {
  integration: Integration;
  object: ObjectConfig;
  onSave: (object: ObjectConfig) => void;
  onClose: () => void;
  isViewMode: boolean;
}

export function useFieldsConfig({
  integration,
  object,
  onSave,
  isViewMode,
  onClose
}: UseFieldsConfigProps) {
  const { eventTrack } = useAnalyticsTracker();

  const [fieldsMetadata, setFieldsMetadata] = useState<ObjectFieldsMetaData | undefined>(undefined);
  const [fieldsObjectConfig, setFieldsObjectConfig] = useState<{ [key: string]: unknown }>({});
  const [destinationTableName, setDestinationTableName] = useState('');

  const loadMode = useRef(object?.loadMode?.toUpperCase());
  const fetchFieldsMeta = useRef(true);

  const { reset, data, selectedCount, filteredListSelectedCount, fetch, updateMemoryData } =
    useInMemoryFilteredList(
      async () => {
        if (fetchFieldsMeta.current) {
          const metaData = await ObjectsConfigAPI.getFieldsMetadata(integration.id).then(res =>
            ObjectFieldsMetaDataFactory(res)
          );

          fetchFieldsMeta.current = false;
          setFieldsMetadata(metaData);
        }

        const getFieldsRequest$ = isViewMode
          ? ObjectsConfigAPI.getObjectFieldsForView.bind(ObjectsConfigAPI)
          : ObjectsConfigAPI.getObjectFields.bind(ObjectsConfigAPI);

        const rawData: ObjectFieldsRawData = await getFieldsRequest$(
          integration.id,
          object.id,
          loadMode.current
        ).then(res => res.data);

        const { fields, ...objectLevelConfig } = rawData;

        setFieldsObjectConfig(objectLevelConfig);
        setDestinationTableName(rawData.destination_table_name);

        return Promise.resolve(ObjectFieldsFactory(rawData));
      },
      (field, params) => {
        return matchPattern(
          [field.fieldName],
          getSearchValue(
            (params?.filters || []) as ListConductorRawFilter[],
            OBJECT_FIELDS_LIST_SEARCH_KEY
          )
        );
      },
      undefined,
      item => {
        return item.status.value === ObjectFieldStatus.ACTIVE;
      }
    );

  const {
    data: filteredFields,
    uiState: fieldsUIState,
    shouldDisableFilters,
    fetch: fetchFieldsList,
    error: fetchFieldsError,
    refetch,
    listConductor
  } = useQuery({
    dataSource$: fetch,
    pollDataSource$: (_, params) => fetch(params).then(res => res.items),
    paginated: true,
    listConductorOptions: {
      handleQueryParams: false,
      filterKeys: [OBJECT_FIELDS_LIST_SEARCH_KEY],
      inMemoryList: true,
      paginationStrategy: new OffsetPaginationStrategy(DEFAULT_LIMIT)
    }
  });

  const { mutationState, mutate: saveChanges } = useMutation({
    mutationTarget$: () =>
      ObjectsConfigAPI.saveFieldsSelection(getFieldsUpdatesPayload(), integration.id, object.id),
    onMutationSuccess: res => {
      if (typeof onSave === 'function') {
        onSave(ObjectFactory(res.data));
      }
    }
  });

  const getFieldsUpdatesPayload = (): FieldsSelectionUpdatesPayload => {
    return {
      fields: (data || []).map(field => {
        return {
          key: field.fieldName,
          value: {
            status: field.status.value,
            ...Object.entries(field.cells).reduce((acc, [cellKey, cell]) => {
              if (cellKey === 'source' || cellKey === 'destination') {
                return acc;
              }

              return {
                ...acc,
                [cellKey]: cell.value
              };
            }, {})
          }
        };
      }),
      config: {
        load_mode: loadMode.current!
      }
    };
  };

  const changeSelectAllCheckedState = (included: boolean) => {
    (data || []).forEach(field => {
      const newField = field;

      if ((filteredFields || []).findIndex(f => f.fieldName === field.fieldName) < 0) {
        return;
      }

      if (newField.status.isEditable) {
        newField.status.value = included ? ObjectFieldStatus.ACTIVE : ObjectFieldStatus.INACTIVE;
      }
    });

    updateMemoryData(data);

    refetch();
  };

  const changeCheckedState = (field: ObjectField, included: boolean) => {
    const newField = { ...field };

    newField.status.value = included ? ObjectFieldStatus.ACTIVE : ObjectFieldStatus.INACTIVE;

    updateMemoryData(data);

    refetch();
  };

  const updateFieldProperty = (field: ObjectField, key: string, value: unknown) => {
    const newField = { ...field };

    newField.cells[key].value = value;

    updateMemoryData(data);

    refetch();
  };

  const nextClickHandler = () => {
    listConductor?.dispatch(new ListNextPage());
  };

  const previousClickHandler = () => {
    listConductor?.dispatch(new ListPrevPage());
  };

  const onSearch = useCallback((term: SearchValueType) => {
    const searchTerm = typeof term === 'string' ? term : term.value;

    eventTrack({
      action: PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_CONFIG_FIELDS_SEARCH,
      properties: {
        searchTerm,
        pipelineId: integration.id
      }
    });

    listConductor?.dispatch(
      new FilterList([
        {
          filter: OBJECT_FIELDS_LIST_SEARCH_KEY,
          operator: typeof term === 'string' ? DEFAULT_STRING_OPERATOR.value : term.operator,
          value: searchTerm
        }
      ])
    );
  }, []);

  const closeFieldsConfig = () => {
    eventTrack({
      action: PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_FIELDS_CLOSE_CLICK,
      properties: {
        pipelineId: integration.id
      }
    });

    onClose();
  };

  const applyChanges = () => {
    eventTrack({
      action:
        PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_FIELDS_APPLY_CHANGES_CLICK,
      properties: {
        pipelineId: integration.id
      }
    });

    saveChanges();
  };

  const onChangeObjectConfigMetaOption = (config: string, value: string) => {
    if (config === 'load_mode') {
      loadMode.current = value;
      reset();
      fetchFieldsList();
    }
  };

  const { totalCount } = listConductor?.getListMeta().pagination as PaginationData;

  const isListIndeterminate =
    filteredListSelectedCount > 0 && filteredListSelectedCount < totalCount;
  const isListIncluded = filteredListSelectedCount === totalCount;

  return {
    selectedCount,
    filteredListSelectedCount,
    totalCount,
    isListIncluded,
    isListIndeterminate,
    listConductor,
    destinationTableName,
    fieldsMetadata,
    fieldsObjectConfig,
    filteredFields,
    fieldsUIState,
    shouldDisableFilters,
    fetchFieldsList,
    fetchFieldsError,
    mutationState,
    saveChanges,
    changeCheckedState,
    changeSelectAllCheckedState,
    updateFieldProperty,
    nextClickHandler,
    previousClickHandler,
    applyChanges,
    closeFieldsConfig,
    onChangeObjectConfigMetaOption,
    onSearch
  };
}
